import { Search } from '@shared/components/ui/Search';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  handleDownloadExcel: () => void;
  isDownloading?: boolean;
}

export const FiltersSection = ({ handleSearch, handleDownloadExcel, isDownloading = false }: FiltersSectionProps) => {
  const { t } = useTranslation('productsWithoutStockModule');

  // 🎯 Hook useQueryParams para sincronizar con URL
  const { params, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
  });

  // 🎯 Estado local sincronizado con URL
  const [inputValue, setInputValue] = useState(params.search ?? '');

  // 🎯 Efecto para sincronizar con cambios en URL
  useEffect(() => {
    const searchFromUrl = params.search ?? '';
    setInputValue((prev) => (prev !== searchFromUrl ? searchFromUrl : prev));

    handleSearch(searchFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search]);

  // 🎯 Handler para el cambio del input - Solo afecta al estado local
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // 🎯 Handler para ejecutar búsqueda - Solo comunica al padre cuando necesario
  const handleSearchSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    updateParam('search', trimmedValue);
    handleSearch(trimmedValue);
  }, [inputValue, updateParam, handleSearch]);

  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <div className="flex-1 min-w-0">
        <Search
          placeholder={t('search.placeholder')}
          value={inputValue}
          onChange={handleInputChange}
          onEnter={handleSearchSubmit}
          dataTestId="search"
          inputDataTestId="search-input"
          buttonIconClassName="light:!text-lines"
          buttonClassName="light:!border-lines light:hover:!text-icons"
          className=" light:!border-lines"
        />
      </div>
      {/* 🎯 Botón de descarga de Excel */}
      <Button
        onClick={handleDownloadExcel}
        type="button"
        size="sm"
        variant="outline"
        disabled={isDownloading}
        className="!py-[9px] !px-3 text-icons transition-opacity duration-200 bg-background flex-shrink-0 light:!text-lines light:!border-lines"
        data-testid="download-excel-button"
      >
        <FontAwesomeIcon icon={faFileArrowDown} className={`${isDownloading ? 'animate-pulse' : ''}`} />
      </Button>
    </div>
  );
};
